/**
 * Org-wide daily token helpers.
 * Tokens restart at 1 each calendar day (local time) per organization.
 */

/** YYYY-MM-DD in the user's local timezone (never UTC via toISOString). */
export const toLocalDateString = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Prefer appointment calendar fields only.
 * Do NOT use createdAt here — undated / token-table rows must not pollute today's max.
 */
const getAppointmentDateTimeRaw = (appointmentOrData, { allowCreatedAt = false } = {}) => {
  if (appointmentOrData == null) return null;
  if (
    typeof appointmentOrData === "string" ||
    appointmentOrData instanceof Date ||
    typeof appointmentOrData === "number"
  ) {
    return appointmentOrData;
  }
  const primary =
    appointmentOrData.appointmentDateTime ||
    appointmentOrData.appointment_date_time ||
    appointmentOrData.appointmentDate ||
    appointmentOrData.date ||
    appointmentOrData.tokenDate ||
    appointmentOrData.token_date ||
    null;
  if (primary != null && primary !== "") return primary;
  if (allowCreatedAt) {
    return (
      appointmentOrData.createdAt ||
      appointmentOrData.created_at ||
      null
    );
  }
  return null;
};

/**
 * Resolve the calendar day used when *assigning* a new token.
 * Falls back to today when appointment data has no date.
 */
export const resolveTokenDate = (appointmentOrData, now = new Date()) => {
  const raw = getAppointmentDateTimeRaw(appointmentOrData, {
    allowCreatedAt: true,
  });
  if (raw == null || raw === "") {
    return toLocalDateString(now);
  }
  return resolveRawToLocalDate(raw) || toLocalDateString(now);
};

/**
 * Strict date for *matching existing records* into a daily bucket.
 * Returns null when the row has no reliable appointment/token date —
 * such rows must be ignored so they cannot push the day's first token to 2+.
 */
export const resolveTokenDateStrict = (appointmentOrData) => {
  const raw = getAppointmentDateTimeRaw(appointmentOrData, {
    allowCreatedAt: false,
  });
  if (raw == null || raw === "") return null;
  return resolveRawToLocalDate(raw);
};

const resolveRawToLocalDate = (raw) => {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    // Local datetime without zone: "2026-07-31T10:20:00" — use date prefix
    if (
      /^\d{4}-\d{2}-\d{2}T/.test(trimmed) &&
      !/[zZ]$/.test(trimmed) &&
      !/[+-]\d{2}:?\d{2}$/.test(trimmed)
    ) {
      return trimmed.slice(0, 10);
    }
  }
  return toLocalDateString(raw);
};

const parsePositiveToken = (payload) => {
  if (payload == null) return null;
  const candidates = [
    payload.tokenNumber,
    payload.token_number,
    payload.token,
    typeof payload === "number" || typeof payload === "string" ? payload : null,
  ];
  for (const val of candidates) {
    if (val == null || val === "") continue;
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

export const appointmentMatchesTokenDate = (appointment, tokenDate) => {
  if (!appointment || !tokenDate) return false;
  const resolved = resolveTokenDateStrict(appointment);
  if (!resolved) return false;
  return resolved === tokenDate;
};

const sameAppointmentId = (row, appointmentId) => {
  if (appointmentId == null || !row) return false;
  return (
    String(row.id) === String(appointmentId) ||
    String(row.appointmentId) === String(appointmentId) ||
    String(row.appointment_id) === String(appointmentId)
  );
};

/** Max token number already assigned for the given local YYYY-MM-DD (org list). */
export const getMaxDailyTokenNumber = (
  records,
  tokenDate,
  { excludeAppointmentId = null } = {}
) => {
  let max = 0;
  for (const row of records || []) {
    if (!row) continue;
    if (sameAppointmentId(row, excludeAppointmentId)) continue;
    if (!appointmentMatchesTokenDate(row, tokenDate)) continue;
    const n = parsePositiveToken(row);
    if (n != null && n > max) max = n;
  }
  return max;
};

export const computeNextDailyTokenNumber = (
  records,
  tokenDate,
  options = {}
) => getMaxDailyTokenNumber(records, tokenDate, options) + 1;

export const isTokenTakenByOther = (
  records,
  tokenDate,
  tokenNumber,
  excludeAppointmentId
) => {
  const target = Number(tokenNumber);
  if (!Number.isFinite(target) || target <= 0) return false;
  for (const row of records || []) {
    if (!row) continue;
    if (sameAppointmentId(row, excludeAppointmentId)) continue;
    if (!appointmentMatchesTokenDate(row, tokenDate)) continue;
    if (parsePositiveToken(row) === target) return true;
  }
  return false;
};

export const findRecordTokenNumber = (records, appointmentId) => {
  if (appointmentId == null) return null;
  for (const row of records || []) {
    if (!sameAppointmentId(row, appointmentId)) continue;
    const n = parsePositiveToken(row);
    if (n != null) return n;
  }
  return null;
};
